import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Plus, Eye, Archive, Radio, Loader2, Info } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost } from '../../lib/auth'
import { CampaignForm } from '../../components/llm/CampaignForm'

interface Campaign {
  id: number
  name: string
  brand: string
  status: 'active' | 'archived'
  queries: any[]
  _count: { audits: number }
  latestScore: number | null
  createdAt: string
  updatedAt: string
}

function scoreColor(score: number | null): string {
  if (score === null) return 'text-[#8B8FA7] bg-[#F4F5FA]'
  if (score >= 60) return 'text-emerald-700 bg-emerald-50'
  if (score >= 40) return 'text-amber-700 bg-amber-50'
  return 'text-red-700 bg-red-50'
}

export default function LlmCampaignList() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [archivingId, setArchivingId] = useState<number | null>(null)

  const { data, loading, error, refetch } = useApi<Campaign[]>(
    projectId ? `/api/llm/campaigns?clientId=${projectId}` : ''
  )

  const campaigns = asArray(data)
  const activeCampaigns = campaigns.filter(c => c.status === 'active')

  if (error === 'Unauthorized' || error === '401') {
    navigate('/login')
    return null
  }

  const handleArchive = async (campaignId: number) => {
    if (!confirm('Archiver cette campagne ?')) return
    setArchivingId(campaignId)
    try {
      const res = await fetch(`/api/llm/campaigns/${campaignId}/archive`, {
        method: 'PATCH',
        credentials: 'include',
      })
      if (res.ok) {
        refetch()
      }
    } finally {
      setArchivingId(null)
    }
  }

  const handleCreateSuccess = (campaign: any) => {
    setShowCreateForm(false)
    refetch()
    navigate(`/p/${projectId}/ai/llm/${campaign.id}`)
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-[#6366F1]" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-red-700">Erreur lors du chargement des campagnes : {error}</p>
        <button
          onClick={refetch}
          className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
        >
          R&eacute;essayer
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2559]">Visibilit&eacute; LLM</h1>
          <p className="mt-1 text-sm text-[#555A73]">
            Mesurez la visibilit&eacute; de votre marque dans les r&eacute;ponses des mod&egrave;les de langage
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#5457E5] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nouvelle campagne
        </button>
      </div>

      {/* Info banner */}
      <div className="rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] px-5 py-3 flex items-start gap-3">
        <Info size={16} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-[#555A73] leading-relaxed">
          Créez une campagne pour mesurer la fréquence à laquelle votre marque est citée par ChatGPT, Gemini et Claude. Chaque audit interroge les LLMs avec la <span className="font-medium text-[#1B2559]">recherche web en temps réel</span> — les résultats reflètent ce qu'un utilisateur obtiendrait en posant la même question aujourd'hui.
        </p>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="rounded-xl border border-[#E2E4ED] bg-white p-6 shadow-sm">
          <CampaignForm
            clientId={Number(projectId)}
            onSuccess={handleCreateSuccess}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* Empty state */}
      {campaigns.length === 0 && !showCreateForm && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-[#E2E4ED] bg-white py-20">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F4F5FA]">
            <Radio className="h-8 w-8 text-[#8B8FA7]" />
          </div>
          <h2 className="mt-5 text-lg font-semibold text-[#1B2559]">Aucune campagne LLM</h2>
          <p className="mt-2 max-w-sm text-center text-sm text-[#555A73]">
            Suivez la visibilit&eacute; de votre marque dans les r&eacute;ponses de ChatGPT, Gemini et d&rsquo;autres mod&egrave;les.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#6366F1] px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#5457E5] transition-colors"
          >
            <Plus className="h-4 w-4" />
            Cr&eacute;er votre premi&egrave;re campagne
          </button>
        </div>
      )}

      {/* Campaigns table */}
      {campaigns.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-[#E2E4ED] bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E2E4ED] bg-[#F9FAFB]">
                <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Nom
                </th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Marque
                </th>
                <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Requ&ecirc;tes
                </th>
                <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Dernier score
                </th>
                <th className="px-6 py-3.5 text-center text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Statut
                </th>
                <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-[#555A73]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E4ED]">
              {campaigns.map(campaign => (
                <tr
                  key={campaign.id}
                  className="hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                  onClick={() => navigate(`/p/${projectId}/ai/llm/${campaign.id}`)}
                >
                  <td className="px-6 py-4">
                    <span className="font-medium text-[#1B2559]">{campaign.name}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-[#555A73]">{campaign.brand}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-sm text-[#555A73]">{(campaign.queries ?? []).length}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${scoreColor(campaign.latestScore)}`}
                    >
                      {campaign.latestScore !== null ? `${campaign.latestScore}%` : '\u2014'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {campaign.status === 'active' ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-[#F4F5FA] px-2.5 py-0.5 text-xs font-semibold text-[#8B8FA7]">
                        Archiv&eacute;e
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/p/${projectId}/ai/llm/${campaign.id}`)
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-[#6366F1] hover:bg-[#6366F1]/5 transition-colors"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Voir
                      </button>
                      {campaign.status === 'active' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleArchive(campaign.id)
                          }}
                          disabled={archivingId === campaign.id}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-[#555A73] hover:bg-[#F4F5FA] transition-colors disabled:opacity-50"
                        >
                          {archivingId === campaign.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Archive className="h-3.5 w-3.5" />
                          )}
                          Archiver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
